import React, { useState, useEffect, useCallback } from "react";
import { Icon } from "@iconify/react";
import useApi from "../../hook/useApi";
import { toast } from "react-toastify";
import Loader from "../Loader/Loader";
import SearchableSelect from "../Common/SearchableSelect";
import Paggination from "../Paggination";

const ManageQtyLayer = () => {
  const {
    AsyncGetApiCall,
    AsyncPostApiCall,
    AsyncDeleteApiCall,
    AsyncPatchApiCall,
  } = useApi();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [dynamicSegments, setDynamicSegments] = useState([]);
  const [scriptsList, setScriptsList] = useState([]);
  const [accountTypes, setAccountTypes] = useState([]);

  // Panel visibility
  const [showAddForm, setShowAddForm] = useState(false);
  const [showBulkDelete, setShowBulkDelete] = useState(false);

  // Bulk Delete State
  const [bulkDelete, setBulkDelete] = useState({
    accountType: "",
    segment: "",
  });
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);

  // Add Form State
  const [formData, setFormData] = useState({
    accountType: "",
    segment: "",
    script: "",
    positionLimit: "",
    minOrder: "",
    maxOrder: "",
    valueType: "quantity",
  });

  // Filter State
  const [filters, setFilters] = useState({
    accountType: "",
    segment: "",
    script: "",
    search: "",
  });

  // Debounced Input State
  const [searchInput, setSearchInput] = useState("");
  const [scriptInput, setScriptInput] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setFilters((prev) => {
        if (prev.search !== searchInput || prev.script !== scriptInput) {
          setPage(1);
          return { ...prev, search: searchInput, script: scriptInput };
        }
        return prev;
      });
    }, 500);
    return () => clearTimeout(handler);
  }, [searchInput, scriptInput]);

  // Pagination State
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const query = { ...filters, page, limit };
    const result = await AsyncGetApiCall(
      "/trading-settings/quantity/list",
      query,
    );
    if (result?.success) {
      setData(result.data || []);
      if (result.pagination) {
        setTotalPages(result.pagination.totalPages);
        setTotalItems(result.pagination.total);
      }
    } else {
      toast.error(result?.message || "Failed to fetch data");
    }
    setLoading(false);
  }, [AsyncGetApiCall, filters, page, limit]);

  const fetchSegments = useCallback(async () => {
    const result = await AsyncGetApiCall(
      "/trading-settings/segments/list?status=active&inSelectMode=true",
    );
    if (result?.success) {
      setDynamicSegments(result?.data || []);
    }
  }, [AsyncGetApiCall]);

  const fetchAccountTypes = useCallback(async () => {
    const result = await AsyncGetApiCall("/trading-settings/account-type");
    if (result?.success) setAccountTypes(result.data || []);
  }, [AsyncGetApiCall]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchSegments();
    fetchAccountTypes();
  }, [fetchSegments, fetchAccountTypes]);

  const fetchScripts = async (segment) => {
    if (!segment) {
      setScriptsList([]);
      return;
    }
    const result = await AsyncGetApiCall(
      `/trading-settings/scripts?segment=${segment}&limitVal=1000`,
    );
    if (result?.success) setScriptsList(result.data || []);
  };

  const handleSegmentChange = (e) => {
    const segment = e.target.value;
    let valueType = "quantity";
    if (
      segment.toUpperCase().includes("MCX") ||
      segment.toUpperCase().includes("NFO")
    )
      valueType = "lot";
    setFormData({ ...formData, segment, script: "", valueType });
    if (segment) fetchScripts(segment);
    else setScriptsList([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.accountType || !formData.segment || !formData.script) {
      toast.error("Please fill required fields");
      return;
    }
    setLoading(true);
    const payload = {
      ...formData,
      positionLimit: Number(formData.positionLimit),
      minOrder: Number(formData.minOrder),
      maxOrder: Number(formData.maxOrder),
    };
    let result;
    if (editingId) {
      result = await AsyncPatchApiCall(
        `/trading-settings/quantity/update/${editingId}`,
        payload,
      );
    } else {
      result = await AsyncPostApiCall(
        "/trading-settings/quantity/bulk-create",
        payload,
      );
    }
    if (result?.success) {
      toast.success(
        editingId
          ? "Updated successfully"
          : result.message || "Added successfully",
      );
      setFormData({
        accountType: "",
        segment: "",
        script: "",
        positionLimit: "",
        minOrder: "",
        maxOrder: "",
        valueType: "quantity",
      });
      setEditingId(null);
      setScriptsList([]);
      setShowAddForm(false);
      fetchData();
    } else {
      toast.error(
        result?.message || (editingId ? "Failed to update" : "Failed to add"),
      );
    }
    setLoading(false);
  };

  const handleBulkDelete = async () => {
    if (!bulkDelete.accountType && !bulkDelete.segment) {
      toast.error(
        "Please select at least Account Type or Segment to bulk delete",
      );
      return;
    }
    const msg = `Delete ALL quantity settings${bulkDelete.accountType ? ` for "${bulkDelete.accountType}"` : ""}${bulkDelete.segment ? ` in segment "${bulkDelete.segment}"` : ""}?`;
    if (!window.confirm(msg)) return;
    setBulkDeleteLoading(true);
    const filter = {};
    if (bulkDelete.accountType) filter.accountType = bulkDelete.accountType;
    if (bulkDelete.segment) filter.segment = bulkDelete.segment;
    const result = await AsyncDeleteApiCall(
      "/trading-settings/quantity/bulk-delete",
      filter,
    );
    if (result?.success) {
      toast.success(result.message || "Bulk delete successful");
      setBulkDelete({ accountType: "", segment: "" });
      setShowBulkDelete(false);
      fetchData();
    } else {
      toast.error(result?.message || "Bulk delete failed");
    }
    setBulkDeleteLoading(false);
  };

  const handleEdit = (item) => {
    setEditingId(item._id);
    setFormData({
      accountType: item.accountType?._id || item.accountType,
      segment: item.segment,
      script: item.script,
      positionLimit: item.positionLimit,
      minOrder: item.minOrder,
      maxOrder: item.maxOrder,
      valueType: item.valueType || "quantity",
    });
    fetchScripts(item.segment);
    setShowAddForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setFormData({
      accountType: "",
      segment: "",
      script: "",
      positionLimit: "",
      minOrder: "",
      maxOrder: "",
      valueType: "quantity",
    });
    setScriptsList([]);
    setShowAddForm(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this setting?"))
      return;
    setLoading(true);
    const result = await AsyncDeleteApiCall(
      `/trading-settings/quantity/delete/${id}`,
    );
    if (result?.success) {
      toast.success("Deleted successfully");
      fetchData();
    } else toast.error(result?.message || "Delete failed");
    setLoading(false);
  };

  const handleExportCSV = () => {
    if (data.length === 0) {
      toast.warn("No data to export");
      return;
    }
    const headers = [
      "segment",
      "script_name",
      "position",
      "min_order",
      "max_order",
      "quantity_lot",
    ];
    const csvContent = [
      headers.join(","),
      ...data.map((item) =>
        [
          item.segment,
          item.script,
          item.positionLimit,
          item.minOrder,
          item.maxOrder,
          item.valueType,
        ].join(","),
      ),
    ].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "standard_quantity_settings.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      {loading && <Loader />}

      {/* ── Action Bar ─────────────────────────────────────────── */}
      <div className="d-flex align-items-center gap-2 mb-16 flex-wrap">

        <div className="d-flex align-items-center gap-2">
          <span className="text-secondary-light text-sm">
            Rows per page:
          </span>
          <select
            className="form-select form-select-sm radius-8"
            style={{ width: "70px" }}
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
          >
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
          </select>
        </div>

        <button
          type="button"
          className={`btn btn-sm radius-8 d-flex align-items-center gap-1 ${showAddForm ? "btn-primary" : "btn-outline-primary"}`}
          onClick={() => {
            setShowAddForm((v) => !v);
            if (showBulkDelete) setShowBulkDelete(false);
          }}
        >
          <Icon icon={showAddForm ? "lucide:x" : "lucide:plus"} />
          {editingId ? "Edit Setting" : showAddForm ? "Close" : "Add Setting"}
        </button>

        <button
          type="button"
          className={`btn btn-sm radius-8 d-flex align-items-center gap-1 ${showBulkDelete ? "btn-danger" : "btn-outline-danger"}`}
          onClick={() => {
            setShowBulkDelete((v) => !v);
            if (showAddForm) setShowAddForm(false);
          }}
        >
          <Icon icon="lucide:trash-2" />
          {showBulkDelete ? "Close Bulk Delete" : "Bulk Delete"}
        </button>
      </div>

      {/* ── Add / Edit Form (collapsible) ─────────────────────── */}
      {showAddForm && (
        <div className="card border shadow-sm radius-16 ledger-table-card">
          <div className="card-body p-24 pb-0 position-relative" style={{ zIndex: 10 }}>
            <div className="d-flex justify-content-between align-items-center mb-12">
              <h6 className="mb-0 text-primary-600 text-sm">
                {editingId
                  ? "Edit Quantity Setting"
                  : "Add New Quantity Setting"}
              </h6>
              {editingId && (
                <button
                  className="btn btn-sm btn-outline-danger radius-8"
                  onClick={handleCancelEdit}
                >
                  Cancel Edit
                </button>
              )}
            </div>
            <form onSubmit={handleSubmit} className="row g-2 align-items-end">
              <div className="col-md-2">
                <label className="form-label text-xs fw-bold text-secondary-light mb-4">
                  ACCOUNT TYPE
                </label>
                <SearchableSelect
                  placeholder="Select Account Type"
                  value={formData.accountType}
                  onChange={(e) =>
                    setFormData({ ...formData, accountType: e.target.value })
                  }
                  options={accountTypes.map((type) => ({
                    label: type.name,
                    value: type._id,
                  }))}
                />
              </div>
              <div className="col-md-2">
                <label className="form-label text-xs fw-bold text-secondary-light mb-4">
                  SEGMENT
                </label>
                <SearchableSelect
                  placeholder="Select Segment"
                  value={formData.segment}
                  onChange={handleSegmentChange}
                  options={dynamicSegments.map((s) => ({
                    label: s.displayName,
                    value: s.name,
                  }))}
                />
              </div>
              <div className="col-md-2">
                <label className="form-label text-xs fw-bold text-secondary-light mb-4">
                  SCRIPT
                </label>
                <SearchableSelect
                  placeholder={
                    formData.segment ? "Select Script" : "Select Segment First"
                  }
                  options={
                    formData.segment
                      ? [
                        { label: "All Scripts", value: "all" },
                        ...scriptsList.map((s) => ({
                          label: `${s.name} (${s.exchange})`,
                          value: s.name,
                        })),
                      ]
                      : []
                  }
                  value={formData.script}
                  onChange={(e) =>
                    setFormData({ ...formData, script: e.target.value })
                  }
                  disabled={!formData.segment}
                />
              </div>
              <div className="col-md-1">
                <label className="form-label text-xs fw-bold text-secondary-light mb-4">
                  POS LIMIT
                </label>
                <input
                  type="number"
                  className="form-control form-control-sm radius-8"
                  value={formData.positionLimit}
                  onChange={(e) =>
                    setFormData({ ...formData, positionLimit: e.target.value })
                  }
                />
              </div>
              <div className="col-md-1">
                <label className="form-label text-xs fw-bold text-secondary-light mb-4">
                  MIN
                </label>
                <input
                  type="number"
                  className="form-control form-control-sm radius-8"
                  value={formData.minOrder}
                  onChange={(e) =>
                    setFormData({ ...formData, minOrder: e.target.value })
                  }
                />
              </div>
              <div className="col-md-1">
                <label className="form-label text-xs fw-bold text-secondary-light mb-4">
                  MAX
                </label>
                <input
                  type="number"
                  className="form-control form-control-sm radius-8"
                  value={formData.maxOrder}
                  onChange={(e) =>
                    setFormData({ ...formData, maxOrder: e.target.value })
                  }
                />
              </div>
              <div className="col-md-1">
                <button
                  type="submit"
                  className="btn btn-sm btn-primary radius-8 w-100"
                >
                  {editingId
                    ? "Update"
                    : formData.script === "all"
                      ? "Bulk Add"
                      : "Add"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Bulk Delete (collapsible) ────────────────────────── */}
      {showBulkDelete && (
        <div
          className="card radius-12 border-0 shadow-sm mb-16"
          style={{ borderLeft: "3px solid #dc3545" }}
        >
          <div className="card-body p-16">
            <p className="text-secondary-light text-xs mb-12">
              Select filters and delete all matching records. Leave blank to
              match all values.
            </p>
            <div className="row g-2 align-items-end">
              <div className="col-md-3">
                <label className="form-label text-xs fw-bold text-secondary-light mb-4">
                  ACCOUNT TYPE
                </label>
                <SearchableSelect
                  placeholder="All Account Types"
                  value={bulkDelete.accountType}
                  onChange={(e) =>
                    setBulkDelete({
                      ...bulkDelete,
                      accountType: e.target.value,
                    })
                  }
                  options={accountTypes.map((type) => ({
                    label: type.name,
                    value: type._id,
                  }))}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label text-xs fw-bold text-secondary-light mb-4">
                  SEGMENT
                </label>
                <SearchableSelect
                  placeholder="All Segments"
                  value={bulkDelete.segment}
                  onChange={(e) =>
                    setBulkDelete({ ...bulkDelete, segment: e.target.value })
                  }
                  options={dynamicSegments.map((s) => ({
                    label: s.displayName,
                    value: s.name,
                  }))}
                />
              </div>
              <div className="col-md-2">
                <button
                  type="button"
                  className="btn btn-sm btn-danger radius-8 w-100 d-flex align-items-center justify-content-center gap-1"
                  onClick={handleBulkDelete}
                  disabled={bulkDeleteLoading}
                >
                  <Icon icon="lucide:trash-2" />
                  {bulkDeleteLoading ? "Deleting..." : "Delete Matching"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Table ──────────────────────────────────────────────── */}
      <div className="card radius-16 border-0 shadow-sm">
        <div className="card-header bg-primary-600 py-12 px-16 d-flex align-items-center justify-content-between position-relative">
          <h6 className="text-white mb-0 text-sm">
            Qty Settings ({data.length})
          </h6>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <button
              type="button"
              className="btn btn-sm btn-success radius-8 d-flex align-items-center gap-1"
              onClick={handleExportCSV}
            >
              <Icon icon="lucide:download" /> CSV
            </button>
            <button
              type="button"
              className="btn btn-sm btn-danger radius-8 d-flex align-items-center gap-1"
              onClick={(e) => {
                setFilters({
                  accountType: "",
                  segment: "",
                  script: "",
                  search: "",
                });
                setSearchInput("");
                setScriptInput("");
                setPage(1);
                e.currentTarget.blur();
              }}
              title="Clear Filters"
            >
              <Icon icon="lucide:rotate-ccw" /> Clear
            </button>
            <div className="flex-grow-1" style={{ minWidth: "130px" }}>
              <SearchableSelect
                placeholder="All Types"
                value={filters.accountType}
                onChange={(e) =>
                  setFilters({ ...filters, accountType: e.target.value })
                }
                options={accountTypes.map((type) => ({
                  label: type.name,
                  value: type._id,
                }))}
              />
            </div>
            <div className="flex-grow-1" style={{ minWidth: "130px" }}>
              <SearchableSelect
                placeholder="All Segments"
                value={filters.segment}
                onChange={(e) =>
                  setFilters({ ...filters, segment: e.target.value })
                }
                options={dynamicSegments.map((s) => ({
                  label: s.name,
                  value: s.name,
                }))}
              />
            </div>
            <div className="flex-grow-1" style={{ minWidth: "150px" }}>
              <input
                type="text"
                className="form-control form-control-sm radius-8"
                placeholder="Filter by Script"
                value={scriptInput}
                onChange={(e) => {
                  setScriptInput(e.target.value.toUpperCase());
                }}
              />
            </div>
            <div className="flex-grow-1" style={{ minWidth: "120px" }}>
              <input
                type="text"
                className="form-control form-control-sm radius-8"
                placeholder="Search..."
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                }}
              />
            </div>
          </div>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table bordered-table mb-0 sm-table">
              <thead>
                <tr>
                  <th>ACCOUNT TYPE</th>
                  <th>SEGMENT</th>
                  <th>SCRIPT</th>
                  <th>POS LIMIT</th>
                  <th>MIN</th>
                  <th>MAX</th>
                  <th>TYPE</th>
                  <th className="text-center">EDIT</th>
                  <th className="text-center">DEL</th>
                </tr>
              </thead>
              <tbody>
                {data.map((item) => (
                  <tr key={item._id}>
                    <td className="text-secondary-light">
                      {item.accountType?.name || item.accountType}
                    </td>
                    <td className="text-secondary-light">{item.segment}</td>
                    <td className="text-secondary-light fw-bold">
                      {item.script}
                    </td>
                    <td className="text-secondary-light">
                      {item.positionLimit}
                    </td>
                    <td className="text-secondary-light">{item.minOrder}</td>
                    <td className="text-secondary-light">{item.maxOrder}</td>
                    <td className="text-secondary-light">{item.valueType}</td>
                    <td className="text-center">
                      <button
                        className="btn btn-sm btn-info radius-8 p-6"
                        title="Edit"
                        onClick={() => handleEdit(item)}
                      >
                        <Icon icon="lucide:edit" />
                      </button>
                    </td>
                    <td className="text-center">
                      <button
                        className="btn btn-sm btn-danger radius-8 p-6"
                        title="Delete"
                        onClick={() => handleDelete(item._id)}
                      >
                        <Icon icon="lucide:trash-2" />
                      </button>
                    </td>
                  </tr>
                ))}
                {data.length === 0 && (
                  <tr>
                    <td
                      colSpan="9"
                      className="text-center py-5 text-secondary-light"
                    >
                      No settings found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        {/* Pagination Footer */}
        {totalItems > 0 && (
          <div className="card-footer bg-white py-12 px-16 d-flex flex-column gap-3 border-top">
            <Paggination
              total={totalItems}
              totalPages={totalPages}
              currentPage={page}
              setPageNo={setPage}
              startFrom={(page - 1) * limit + 1}
              endTo={Math.min(page * limit, totalItems)}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default ManageQtyLayer;
